import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { RoutePath } from '@quizzach/interfaces';
import { LogoWordmark } from '@quizzach/assets';
import { AuthorMarkBox } from '@quizzach/styles';

interface AuthorMarkProps {
	displayName?: string;
	displayNameSlug?: string;
	/** true when rendered over a photo / dark surface */
	onDark?: boolean;
	/** wrap in a link to the author page (default true) */
	linked?: boolean;
}

// One place for the collaborator-logo special cases that used to be
// copy-pasted across cards, detail, profile and order pages.
// `base` points at the untinted original; brand-tinted variants live next to
// it as `{base}-brand.png` / `{base}-branddark.png` / `{base}-neon.png`.
const COLLAB_LOGOS: Record<
	string,
	{
		base: string;
		src: string;
		alt: string;
		squarish?: boolean;
		/** artwork reads light at the standard hero height — render larger */
		heroLarge?: boolean;
	}
> = {
	Nogač: {
		base: '/collaborators/collaborator_logo-nogac',
		src: '/collaborators/collaborator_logo-nogac.png',
		alt: 'Nogač',
		squarish: true,
	},
	'Vragolasti Tenis': {
		base: '/collaborators/collaborator_logo-vragolasti',
		src: '/collaborators/collaborator_logo-vragolasti.png',
		alt: 'Vragolasti Tenis',
		heroLarge: true,
	},
	Putoholičari: {
		base: '/collaborators/collaborator_logo-putoholicari',
		src: '/collaborators/collaborator_logo-putoholicari.png',
		alt: 'Putoholičari',
	},
	Goodtalking: {
		base: '/collaborators/collaborator_logo-goodtalking',
		src: '/collaborators/collaborator_logo-goodtalking.png',
		alt: 'Goodtalking',
	},
};

/**
 * Card-row variant: collaborator logos render as pre-tinted brand-color
 * images (plain <img>, no CSS masks — those proved unreliable across
 * browsers/blockers); authors without a logo get the handwritten name.
 * A hidden neon twin is included so over-photo contexts (featured izlog on
 * mobile) can swap to the bright variant purely in CSS.
 */
export const CardAuthorMark = ({ displayName }: { displayName?: string }) => {
	const theme = useTheme() as { mode?: string } | undefined;

	if (!displayName) return null;

	if (displayName === 'quizzach') {
		return (
			<span
				className='author-tint'
				aria-label='quizzach'
				style={{ color: 'var(--teal-dark)' }}
			>
				<LogoWordmark width={66} height={20} />
			</span>
		);
	}

	const logo = COLLAB_LOGOS[displayName];
	if (!logo) return <span className='author-hand'>{displayName}</span>;

	const variant = theme?.mode === 'dark' ? 'branddark' : 'brand';

	return (
		<span
			className={logo.squarish ? 'author-img sq' : 'author-img'}
			role='img'
			aria-label={logo.alt}
		>
			<img className='v-base' src={`${logo.base}-${variant}.png`} alt='' />
			<img className='v-neon' src={`${logo.base}-neon.png`} alt='' />
		</span>
	);
};

/**
 * Page-title variant for /premade/author: the name or logo IS the h1, so it
 * renders bare (no link, no box) and inherits size/color from the heading —
 * collaborator logos come pre-tinted in the brand color like the card rows.
 */
export const HeroAuthorMark = ({ displayName }: { displayName?: string }) => {
	const theme = useTheme() as { mode?: string } | undefined;

	if (!displayName) return null;

	if (displayName === 'quizzach') {
		return (
			<span aria-label='quizzach'>
				<LogoWordmark width={218} height={67} />
			</span>
		);
	}

	const logo = COLLAB_LOGOS[displayName];
	if (!logo) return <span className='hand-name'>{displayName}</span>;

	const variant = theme?.mode === 'dark' ? 'branddark' : 'brand';

	return (
		<img
			className={logo.squarish ? 'sq' : logo.heroLarge ? 'lg' : undefined}
			src={`${logo.base}-${variant}.png`}
			alt={logo.alt}
		/>
	);
};

export const AuthorMark = ({
	displayName,
	displayNameSlug,
	onDark,
	linked = true,
}: AuthorMarkProps) => {
	if (!displayName) return null;

	const inner =
		displayName === 'quizzach' ? (
			<span
				style={{ color: onDark ? 'var(--teal)' : 'var(--logo)' }}
				aria-label='quizzach'
			>
				<LogoWordmark width={78} height={24} />
			</span>
		) : COLLAB_LOGOS[displayName] ? (
			<img
				src={COLLAB_LOGOS[displayName].src}
				alt={COLLAB_LOGOS[displayName].alt}
			/>
		) : (
			<span className='hand-name'>{displayName}</span>
		);

	return (
		<AuthorMarkBox $onDark={onDark}>
			{linked && displayNameSlug ? (
				<Link
					to={`${RoutePath.PremadeQuizzes}/author/${displayNameSlug}`}
					onClick={(e) => e.stopPropagation()}
				>
					{inner}
				</Link>
			) : (
				inner
			)}
		</AuthorMarkBox>
	);
};
